import { supabase } from "@/lib/supabase";
import {
  subscribeToWorkspaceThreads,
  unsubscribeFromWorkspaceThreads,
} from "@/lib/db/utils/supabase/threads";
import useAuthStore from "@/store/authStore";
import { useWorkspaceThreadsStore } from "@/store/workspaceThreadsStore";
import { useEffect, useRef, useState } from "react";

// Module-level storage to persist signature across component remounts
let previousWorkspaceSignature = null;
let cachedWorkspaceThreads = null;

/**
 * Update the cached workspace threads for a specific workspace.
 * Called by useSidebarDndHandlers when reordering to keep cache in sync.
 */
export function updateCachedWorkspaceThreads(workspaceId, threads) {
  if (cachedWorkspaceThreads && workspaceId) {
    cachedWorkspaceThreads = {
      ...cachedWorkspaceThreads,
      [workspaceId]: threads,
    };
  }
}

// Helper to create signature from workspaces
const getWorkspacesSignature = (ws) => {
  if (!ws || ws.length === 0) {
    return null;
  }
  const workspaceIds = ws
    .map((w) => w.id)
    .sort()
    .join(",");
  const threadIds = ws
    .map((w) => (w.workspace_threads || []).sort().join(","))
    .sort()
    .join("|");
  return `${workspaceIds}::${threadIds}`;
};

export function useProjectThreads({
  workspaces,
  isDefaultTitle,
  markThreadAsNew,
}) {
  const { user } = useAuthStore();
  const [workspaceThreads, setWorkspaceThreads] = useState(() => {
    // Initialize with cached threads if available and signature matches
    const currentSig = getWorkspacesSignature(workspaces);
    if (cachedWorkspaceThreads && currentSig === previousWorkspaceSignature) {
      return cachedWorkspaceThreads;
    }
    return {};
  });
  const [isLoadingThreads, setIsLoadingThreads] = useState(true);

  const threadChannelsRef = useRef([]);
  const isSubscribedRef = useRef(false);

  const cleanupAllSubscriptions = () => {
    if (threadChannelsRef.current.length > 0) {
      threadChannelsRef.current.forEach((channel) => {
        try {
          if (
            channel &&
            typeof unsubscribeFromWorkspaceThreads === "function"
          ) {
            unsubscribeFromWorkspaceThreads(channel);
          } else if (
            channel &&
            supabase &&
            typeof supabase.removeChannel === "function"
          ) {
            supabase.removeChannel(channel);
          }
        } catch (error) {
          console.debug(
            "Thread subscription cleanup completed:",
            error.message
          );
        }
      });
      threadChannelsRef.current = [];
    }

    isSubscribedRef.current = false;
  };

  // Fetch initial thread titles in parallel for better performance
  useEffect(() => {
    const fetchWorkspaceThreadTitles = async () => {
      if (!workspaces || workspaces.length === 0) {
        setIsLoadingThreads(false);
        previousWorkspaceSignature = null;
        cachedWorkspaceThreads = null;
        return;
      }

      // Calculate current signature
      const currentSignature = getWorkspacesSignature(workspaces);

      // Check if the signature has actually changed (using module-level storage)
      if (
        currentSignature === previousWorkspaceSignature &&
        previousWorkspaceSignature !== null &&
        cachedWorkspaceThreads
      ) {
        // Signature hasn't changed, use cached threads and skip refetching
        setWorkspaceThreads(cachedWorkspaceThreads);
        setIsLoadingThreads(false);
        return;
      }
      // Update module-level storage with current signature
      previousWorkspaceSignature = currentSignature;

      setIsLoadingThreads(true);

      // Filter workspaces that have threads to fetch
      const workspacesWithThreads = workspaces.filter(
        (workspace) =>
          workspace.workspace_threads && workspace.workspace_threads.length > 0
      );

      if (workspacesWithThreads.length === 0) {
        setWorkspaceThreads({});
        setIsLoadingThreads(false);
        return;
      }

      // Fetch all workspace threads in parallel
      const fetchPromises = workspacesWithThreads.map(async (workspace) => {
        try {
          const { data: workspaceThreadsData, error } = await supabase
            .from("threads")
            .select(
              "id, title, openai_thread_id, status, updated_at, metadata, is_team, is_shared, workspace_id, display_order"
            )
            .in("id", workspace.workspace_threads)
            .order("display_order", { ascending: true })
            .order("updated_at", { ascending: true }); // Fallback for threads without display_order

          if (error) {
            console.error(
              `Error fetching threads for workspace ${workspace.id}:`,
              error
            );
            return { workspaceId: workspace.id, threads: [] };
          }

          const processedThreads = workspaceThreadsData
            .filter(
              (thread) =>
                !thread.metadata?.moved_to_history_at &&
                (thread.is_team === true ||
                  thread.workspace_id === workspace.id)
            )
            .map((thread) => ({
              id: thread.id,
              title: thread.title || `Chat ${thread.id.substring(0, 6)}...`,
              status: thread.status || "active",
              openai_thread_id: thread.openai_thread_id,
              lastMessageAt: thread.updated_at,
              display_order: thread.display_order ?? 0,
              metadata: {
                ...thread.metadata,
                is_shared_thread:
                  thread.is_team === true || thread.is_shared === true,
                shared_in_workspace:
                  thread.is_team === true
                    ? workspace.id
                    : thread.metadata?.shared_in_workspace,
              },
              is_team: thread.is_team,
              is_shared: thread.is_shared,
              workspace_id: thread.workspace_id,
            }))
            .sort((a, b) => {
              // Sort by display_order first, then by updated_at as fallback
              if (a.display_order != null && b.display_order != null) {
                return a.display_order - b.display_order;
              }
              if (a.display_order != null) return -1;
              if (b.display_order != null) return 1;
              // Fallback to updated_at
              const dateA = new Date(a.lastMessageAt || 0);
              const dateB = new Date(b.lastMessageAt || 0);
              return dateA - dateB;
            });

          return { workspaceId: workspace.id, threads: processedThreads };
        } catch (error) {
          console.error(`Error processing workspace ${workspace.id}:`, error);
          return { workspaceId: workspace.id, threads: [] };
        }
      });

      // Wait for all fetches to complete in parallel
      const results = await Promise.all(fetchPromises);

      // Build threadsMap from results
      const threadsMap = {};
      results.forEach(({ workspaceId, threads }) => {
        threadsMap[workspaceId] = threads;
      });

      // Also initialize empty arrays for workspaces without threads
      workspaces.forEach((workspace) => {
        if (!threadsMap[workspace.id]) {
          threadsMap[workspace.id] = [];
        }
      });

      setWorkspaceThreads(threadsMap);
      // Cache the threads for future use
      cachedWorkspaceThreads = threadsMap;
      setIsLoadingThreads(false);
    };

    fetchWorkspaceThreadTitles();
    // The signature check inside prevents refetching when data hasn't actually changed
  }, [workspaces]);

  // Setup real-time subscriptions
  useEffect(() => {
    if (!user?.id || !workspaces || workspaces.length === 0) {
      return;
    }

    // Calculate current signature and extract workspace IDs for comparison
    const currentSignature = getWorkspacesSignature(workspaces);
    const currentWorkspaceIds = currentSignature
      ? currentSignature.split("::")[0]
      : null;
    const previousWorkspaceIds = previousWorkspaceSignature
      ? previousWorkspaceSignature.split("::")[0]
      : null;

    // Only resubscribe if workspace IDs have actually changed
    if (
      isSubscribedRef.current &&
      currentWorkspaceIds === previousWorkspaceIds &&
      previousWorkspaceIds !== null
    ) {
      return;
    }
    cleanupAllSubscriptions();

    const setupThreadSubscriptions = () => {
      try {
        workspaces.forEach((workspace) => {
          if (workspace.id) {
            try {
              const channel = subscribeToWorkspaceThreads(
                workspace.id,
                (updatedThread) => {
                  try {
                    // Get current workspaces from state to avoid stale closure data
                    const currentWorkspaces =
                      useAuthStore.getState().workspaces || [];
                    const currentWorkspace = currentWorkspaces.find(
                      (w) => w.id === workspace.id
                    );

                    setWorkspaceThreads((prevThreads) => {
                      if (prevThreads[workspace.id]) {
                        const existingThreadIndex = prevThreads[
                          workspace.id
                        ].findIndex((t) => t.id === updatedThread.id);

                        if (existingThreadIndex >= 0) {
                          // Remove thread if it's been moved to history or to a different workspace
                          if (
                            updatedThread.metadata?.moved_to_history_at ||
                            (updatedThread.is_team !== true &&
                              updatedThread.workspace_id !== workspace.id)
                          ) {
                            const updatedThreads = [
                              ...prevThreads[workspace.id],
                            ];
                            updatedThreads.splice(existingThreadIndex, 1);
                            return {
                              ...prevThreads,
                              [workspace.id]: updatedThreads,
                            };
                          }

                          const updatedThreads = [...prevThreads[workspace.id]];
                          updatedThreads[existingThreadIndex] = {
                            ...updatedThreads[existingThreadIndex],
                            title:
                              updatedThread.title ||
                              updatedThreads[existingThreadIndex].title,
                            status:
                              updatedThread.status ||
                              updatedThreads[existingThreadIndex].status,
                            updated_at:
                              updatedThread.updated_at ||
                              new Date().toISOString(),
                            display_order:
                              updatedThread.display_order ??
                              updatedThreads[existingThreadIndex].display_order,
                          };

                          return {
                            ...prevThreads,
                            [workspace.id]: updatedThreads,
                          };
                        } else if (
                          // Only add thread if it actually belongs to this workspace
                          // Primary check: thread's workspace_id must match
                          updatedThread.workspace_id === workspace.id &&
                          !updatedThread.metadata?.moved_to_history_at &&
                          !prevThreads[workspace.id]?.some(
                            (t) => t.id === updatedThread.id
                          ) &&
                          // Secondary check: ensure it's not a stale thread from old workspace_threads array
                          // If workspace_threads exists and doesn't include this thread, skip (unless it's a team thread)
                          (updatedThread.is_team === true ||
                            !currentWorkspace?.workspace_threads ||
                            currentWorkspace.workspace_threads.includes(
                              updatedThread.id
                            ))
                        ) {
                          const newThread = {
                            id: updatedThread.id,
                            title:
                              updatedThread.title ||
                              `Chat ${updatedThread.id.substring(0, 6)}...`,
                            status: updatedThread.status || "active",
                            openai_thread_id: updatedThread.openai_thread_id,
                            metadata: updatedThread.metadata,
                            display_order: updatedThread.display_order ?? 0,
                            workspace_id: updatedThread.workspace_id,
                          };

                          if (
                            isDefaultTitle &&
                            !isDefaultTitle(newThread.title)
                          ) {
                            if (markThreadAsNew) {
                              markThreadAsNew(
                                updatedThread.id,
                                newThread.title
                              );
                            }
                          }

                          return {
                            ...prevThreads,
                            [workspace.id]: [
                              ...(prevThreads[workspace.id] || []),
                              newThread,
                            ],
                          };
                        }
                      }

                      return prevThreads;
                    });
                  } catch (error) {
                    console.debug("Error handling thread update:", error);
                  }
                },
                (deletedThread) => {
                  try {
                    setWorkspaceThreads((prevThreads) => {
                      if (prevThreads[workspace.id]) {
                        return {
                          ...prevThreads,
                          [workspace.id]: prevThreads[workspace.id].filter(
                            (t) => t.id !== deletedThread.id
                          ),
                        };
                      }
                      return prevThreads;
                    });
                  } catch (error) {
                    console.debug("Error handling thread deletion:", error);
                  }
                }
              );

              if (channel) {
                threadChannelsRef.current.push(channel);
              }
            } catch (error) {
              console.debug(
                `Error setting up subscription for workspace ${workspace.id}:`,
                error
              );
            }
          }
        });

        isSubscribedRef.current = true;
      } catch (error) {
        console.debug("Error setting up thread subscriptions:", error);
      }
    };

    setupThreadSubscriptions();

    return cleanupAllSubscriptions;
    // The signature check inside prevents resubscriptions when workspace IDs haven't changed
  }, [workspaces, user?.id, isDefaultTitle, markThreadAsNew]);

  // Sync local state to Zustand store for global access
  // This allows useSidebarDndHandlers (in Main.jsx) to access workspace threads
  useEffect(() => {
    const store = useWorkspaceThreadsStore.getState();
    store.setWorkspaceThreads(workspaceThreads);
    store.setIsLoadingThreads(isLoadingThreads);
  }, [workspaceThreads, isLoadingThreads]);

  // Listen for thread reorder events to update local state (for sidebar)
  useEffect(() => {
    const handleThreadsReordered = (event) => {
      const { workspaceId, threads } = event.detail;
      if (workspaceId) {
        setWorkspaceThreads((prev) => ({
          ...prev,
          [workspaceId]: threads,
        }));
      }
    };

    window.addEventListener("workspace-threads-reordered", handleThreadsReordered);
    return () => {
      window.removeEventListener("workspace-threads-reordered", handleThreadsReordered);
    };
  }, []);

  return {
    workspaceThreads,
    setWorkspaceThreads,
    isLoadingThreads,
  };
}
